import { Howler } from 'howler';
export type SoundEffect =
  | 'coin'
  | 'powerup'
  | 'crash'
  | 'laneSwitch'
  | 'gameOver'
  | 'lifeline'
  | 'start';
interface ActiveTone {
  osc: OscillatorNode;
  gain: GainNode;
  expiresAt: number;
}
class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private engineOsc: OscillatorNode | null = null;
  private engineLfo: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private muted = false;
  private volume = 0.6;
  private initialized = false;
  private activeTones: ActiveTone[] = [];
  init(): void {
    if (this.initialized) return;
    if (typeof window === 'undefined') return;
    try {
      const AudioCtor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtor) return;
      this.ctx = new AudioCtor();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : this.volume;
      this.masterGain.connect(this.ctx.destination);
      this.initialized = true;
    } catch (error) {
      console.warn('[Audio] Initialization failed', error);
    }
  }
  resume(): void {
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
  }
  private gcTones(): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    this.activeTones = this.activeTones.filter((tone) => {
      if (tone.expiresAt < now - 0.1) {
        try {
          tone.gain.disconnect();
        } catch {
          /* already disconnected */
        }
        return false;
      }
      return true;
    });
  }
  playSound(effect: SoundEffect): void {
    if (this.muted) return;
    if (!this.initialized) this.init();
    if (!this.ctx || !this.masterGain) return;
    this.resume();
    const ctx = this.ctx;
    const t = ctx.currentTime;
    switch (effect) {
      case 'coin':
        this.tone(880, 0.08, t, 0, 0.28, 'square');
        this.tone(1320, 0.12, t, 0.07, 0.22, 'square');
        break;
      case 'powerup':
        this.tone(523, 0.09, t, 0, 0.22, 'sine');
        this.tone(659, 0.09, t + 0.08, 0, 0.22, 'sine');
        this.tone(784, 0.14, t + 0.16, 0, 0.28, 'sine');
        this.tone(1047, 0.18, t + 0.26, 0, 0.24, 'triangle');
        break;
      case 'crash':
        this.playCrashBurst(ctx, t);
        break;
      case 'laneSwitch':
        this.tone(520, 0.05, t, 0, 0.16, 'triangle');
        break;
      case 'lifeline':
        this.tone(660, 0.12, t, 0, 0.25, 'triangle');
        this.tone(880, 0.12, t + 0.1, 0, 0.25, 'triangle');
        break;
      case 'gameOver':
        this.tone(440, 0.22, t, 0, 0.3, 'square');
        this.tone(330, 0.22, t + 0.18, 0, 0.3, 'square');
        this.tone(220, 0.5, t + 0.36, 0, 0.35, 'sawtooth');
        break;
      case 'start':
        this.tone(330, 0.07, t, 0, 0.22, 'square');
        this.tone(523, 0.12, t + 0.06, 0, 0.25, 'square');
        break;
      default:
        break;
    }
    this.gcTones();
  }
  private playCrashBurst(ctx: AudioContext, t: number): void {
    if (!this.masterGain) return;
    const sampleRate = ctx.sampleRate;
    const bufferSize = Math.floor(sampleRate * 0.45);
    const buffer = ctx.createBuffer(1, bufferSize, sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      const decay = Math.pow(1 - i / bufferSize, 1.6);
      data[i] = (Math.random() * 2 - 1) * decay;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.35;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 900;
    noise.connect(filter).connect(noiseGain).connect(this.masterGain);
    noise.start(t);
    noise.stop(t + 0.5);
    this.tone(220, 0.18, t, 0, 0.25, 'sawtooth');
    this.tone(110, 0.3, t + 0.08, 0, 0.3, 'sawtooth');
  }
  private tone(
    freq: number,
    duration: number,
    startTime: number,
    offset: number,
    peak: number,
    type: OscillatorType
  ): void {
    if (!this.ctx || !this.masterGain) return;
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    const t = startTime + offset;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(peak, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + duration);
    osc.connect(gain).connect(this.masterGain);
    osc.start(t);
    osc.stop(t + duration + 0.05);
    this.activeTones.push({ osc, gain, expiresAt: t + duration + 0.1 });
  }
  startEngine(): void {
    if (!this.initialized) this.init();
    if (!this.ctx || !this.masterGain) return;
    if (this.engineOsc) return;
    this.resume();
    const ctx = this.ctx;
    this.engineOsc = ctx.createOscillator();
    this.engineLfo = ctx.createOscillator();
    this.engineGain = ctx.createGain();
    this.engineFilter = ctx.createBiquadFilter();
    this.engineOsc.type = 'sawtooth';
    this.engineOsc.frequency.value = 80;
    this.engineLfo.type = 'sine';
    this.engineLfo.frequency.value = 6;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 4;
    this.engineLfo.connect(lfoGain).connect(this.engineOsc.frequency);
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 420;
    this.engineGain.gain.value = this.muted ? 0 : 0.045;
    this.engineOsc.connect(this.engineFilter).connect(this.engineGain).connect(this.masterGain);
    this.engineOsc.start();
    this.engineLfo.start();
  }
  setEngineSpeed(speed: number): void {
    if (!this.engineOsc || !this.engineGain) return;
    const safe = Math.max(0, Math.min(speed, 20));
    this.engineOsc.frequency.value = 70 + safe * 5;
    this.engineGain.gain.value = this.muted ? 0 : 0.035 + safe * 0.006;
  }
  stopEngine(): void {
    try {
      if (this.engineOsc) {
        this.engineOsc.stop();
        this.engineOsc.disconnect();
      }
    } catch {
      /* ignore */
    }
    try {
      if (this.engineLfo) {
        this.engineLfo.stop();
        this.engineLfo.disconnect();
      }
    } catch {
      /* ignore */
    }
    try {
      if (this.engineGain) {
        this.engineGain.disconnect();
      }
    } catch {
      /* ignore */
    }
    try {
      if (this.engineFilter) {
        this.engineFilter.disconnect();
      }
    } catch {
      /* ignore */
    }
    this.engineOsc = null;
    this.engineLfo = null;
    this.engineGain = null;
    this.engineFilter = null;
  }
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.masterGain) {
      this.masterGain.gain.value = muted ? 0 : this.volume;
    }
    if (this.engineGain) {
      this.engineGain.gain.value = muted ? 0 : 0.04;
    }
    Howler.mute(muted);
  }
  isMuted(): boolean {
    return this.muted;
  }
  destroy(): void {
    this.stopEngine();
    if (this.ctx) {
      try {
        void this.ctx.close();
      } catch {
        /* ignore */
      }
    }
    this.initialized = false;
  }
}
export const audioManager = new AudioManager();